import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import Modal from "../../Modal/Modal";
import { SearchOutlined } from "@ant-design/icons";
import { CustomCard } from "../../Utils/UI/Text";
import "./DonutChart.css";
import Loader from "./../../Loader/Loader";

function DonutChart({ donutData, isLoading }) {
  const [seriesOriginal, setSeriesOriginal] = useState([]);
  const [series, setSeries] = useState([]);
  const [percents, setPercents] = useState([]);
  const [labels, setLabels] = useState([]);
  const [options, setOptions] = useState({});
  const [visible, setVisible] = useState(false);

  const colors = ["#0319a3", "#8998f5", "#00dd9e"];
  const minValueVisible = 46;

  const getChartWidth = () => {
    const width = window.innerWidth;
    if (width > 992) return 360;
    if (width > 576) return 330;
    return "100%";
  };
  const getModalWidth = () => (window.innerWidth > 576 ? 540 : "100%");
  const [chartWidth, setChartWidth] = useState(getChartWidth());
  const [chartModal, setChartModal] = useState(getModalWidth());
  useEffect(() => {
    const handleResize = () => {
      setChartWidth(getChartWidth());
      setChartModal(getModalWidth());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isLoading || !donutData || !donutData.scopes) {
      setLabels([]);
      setSeries([]);
      setSeriesOriginal([]);
      setPercents([]);
      setOptions({});
      return;
    }

    const emissionsData = donutData.scopes;
    const labelData = [];
    const seriesData = [];
    const originalData = [];
    const percentData = [];

    const keys = Object.keys(emissionsData).filter((k) => k !== "total");
    for (const key of keys) {
      const item = emissionsData[key] || {};
      const quantity = Number(item.quantity || 0);
      const percent = item.percent ?? 0;

      const scopeLabel = key.charAt(0).toUpperCase() + key.slice(1);
      labelData.push(scopeLabel);
      originalData.push(quantity);
      seriesData.push(quantity < minValueVisible ? minValueVisible : quantity);
      percentData.push(percent);
    }

    const newOptions = {
      labels: labelData,
      colors,
      chart: {
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (value, opts) => {
            const index = opts.seriesIndex;
            const percent = percentData[index] ?? 0;
            return `${Number(value).toFixed(2)} ton CO₂ (${percent}%)`;
          },
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          donut: { size: "60%" },
          dataLabels: { minAngleToShowLabel: 0 },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (value, opts) => {
          const index = opts.seriesIndex;
          const percent = percentData[index] ?? 0;
          const quantity = originalData[index] ?? 0;
          if (percent < 1) return "";
          return `${labelData[index]}: ${Number(quantity).toFixed(2)} / ${percent}%`;
        },
        style: {
          fontSize: "14px",
          fontFamily: "MontserratBold",
          fontWeight: "bold",
          colors: ["#041370"],
        },
        background: {
          enabled: true,
          padding: 10,
          borderRadius: 10,
          opacity: 0.85,
        },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "left",
        fontSize: "14px",
        fontFamily: "MontserratBold",
        offsetX: 50,
        offsetY: window.innerWidth < 576 ? 0 : 10,
        formatter: (_, opts) => labelData[opts.seriesIndex],
      },
    };

    setLabels(labelData);
    setSeries(seriesData);
    setSeriesOriginal(originalData);
    setPercents(percentData);
    setOptions(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donutData, isLoading]);

  return (
    <>
      <CustomCard>
        <span className="donut-chart-title">
          Scopes (ton CO₂/%)
          <SearchOutlined onClick={() => setVisible(true)} />
        </span>

        <div className="donut-container">
          {isLoading ? (
            <div className="donut-chart-loader">
              <Loader />
            </div>
          ) : !donutData?.scopes ||
            !seriesOriginal.length ||
            !percents.length ? (
            <div className="no-results">No results found.</div>
          ) : (
            <Chart
              options={options}
              series={series}
              type="donut"
              width={chartWidth}
              height={window.innerWidth > 576 ? 305 : 280}
              style={{ cursor: "pointer" }}
            />
          )}
        </div>

        {donutData?.scopes && (
          <Modal
            width={chartModal}
            title="Scopes (ton CO₂/%)"
            visible={visible}
            onClick={() => setVisible(false)}
            onCancel={() => setVisible(false)}
            cancelButtonProps={{ style: { display: "none" } }}
            okText="Cerrar"
          >
            <div className="donut-modal-content">
              {seriesOriginal.length >= 3 && percents.length >= 3 ? (
                <>
                  <h3>
                    Scope1: {seriesOriginal[0]} / {percents[0]}%
                  </h3>
                  <h3>
                    Scope2: {seriesOriginal[1]} / {percents[1]}%
                  </h3>
                  <h3>
                    Scope3: {seriesOriginal[2]} / {percents[2]}%
                  </h3>
                </>
              ) : (
                <p>Cargando datos...</p>
              )}
            </div>
          </Modal>
        )}
      </CustomCard>
    </>
  );
}

export default DonutChart;
